import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import useApiHelper from "../hooks/useApiHelper";
import { useAuth } from "../contexts/AuthContext";
import { useSignalR } from "../contexts/SignalRContext";
import {
  IChatChannel,
  IChatCreateChannelBody,
  IChatMessage,
  IChatReadState,
  IChatSendMessageBody,
  IChatUser,
} from "../models/Chat";
import { DefaultResponse, DefaultResponseWithData } from "../models/system";

export const chatQueryKeys = {
  users: ["chat", "users"] as const,
  channels: ["chat", "channels"] as const,
  messages: (channelId: string) =>
    ["chat", "channels", channelId, "messages"] as const,
  readState: ["chat", "read-state"] as const,
  // Lightweight `{ total }` summary for the global sidebar badge. Kept
  // separate from `channels` so non-chat routes can render the badge
  // without having to load the full channel list.
  unreadSummary: ["chat", "unread-summary"] as const,
  // Per-channel digest of unread messages for the topbar notifications
  // drawer. Returns the latest unread message embedded so the drawer
  // can render previews without follow-up fetches.
  unreadChannels: ["chat", "unread-channels"] as const,
};

/** Per-channel unread digest row returned by GET /chat/unread-channels. */
export interface IChatUnreadChannel {
  channelId: string;
  channelKind: "channel" | "dm";
  channelName: string;
  memberIds: string[];
  unreadCount: number;
  latestMessageId?: string;
  latestAuthorId?: string;
  latestBody?: string;
  latestCreatedAt?: string;
}

export default function useChat() {
  const { get, post, patch, del } = useApiHelper();
  const { loading: authLoading, loggedIn } = useAuth();
  const queryClient = useQueryClient();

  // Gate every chat query on the auth bootstrap completing - otherwise
  // the very first render fires `/chat/*` with a placeholder Bearer
  // header, the server returns 401, and React Query's default retry
  // backs off for ~10s before the page settles. Mutations don't need
  // the same guard - they're only invoked from user interactions which
  // happen after auth is ready.
  const authReady = !authLoading && loggedIn;

  const users = useQuery({
    queryKey: chatQueryKeys.users,
    queryFn: () => get<DefaultResponseWithData<IChatUser[]>>("/chat/users"),
    staleTime: 1000 * 30,
    refetchInterval: 1000 * 60,
    enabled: authReady,
  });

  const channels = useQuery({
    queryKey: chatQueryKeys.channels,
    queryFn: () => get<DefaultResponseWithData<IChatChannel[]>>("/chat/channels"),
    staleTime: 1000 * 30,
    enabled: authReady,
  });

  const readState = useQuery({
    queryKey: chatQueryKeys.readState,
    queryFn: () => get<DefaultResponseWithData<IChatReadState>>("/chat/read-state"),
    staleTime: 1000 * 30,
    enabled: authReady,
  });

  /** Per-channel message list. Caller passes `enabled=false` until they have
   * an active channel selected so we don't fire requests for every channel. */
  const useMessages = (channelId: string | null) => {
    return useQuery({
      queryKey: channelId
        ? chatQueryKeys.messages(channelId)
        : ["chat", "messages", "none"],
      queryFn: () =>
        get<DefaultResponseWithData<IChatMessage[]>>(
          `/chat/channels/${channelId}/messages`
        ),
      enabled: authReady && !!channelId,
      staleTime: 1000 * 15,
    });
  };

  const sendMessage = useMutation(
    ({
      channelId,
      body,
    }: {
      channelId: string;
      body: IChatSendMessageBody;
    }) =>
      post<IChatSendMessageBody, DefaultResponseWithData<IChatMessage>>(
        `/chat/channels/${channelId}/messages`,
        body
      ),
    {
      onSuccess: (res) => {
        const created = res?.data;
        if (created?.channelId) {
          queryClient.invalidateQueries(chatQueryKeys.messages(created.channelId));
          queryClient.invalidateQueries(chatQueryKeys.channels);
          queryClient.invalidateQueries(chatQueryKeys.unreadSummary);
        }
      },
    }
  );

  const editMessage = useMutation(
    ({ messageId, body }: { messageId: string; body: string }) =>
      patch<DefaultResponseWithData<IChatMessage>>(
        `/chat/messages/${messageId}`,
        { body }
      ),
    {
      onSuccess: (res) => {
        const updated = res?.data;
        if (updated?.channelId) {
          queryClient.invalidateQueries(chatQueryKeys.messages(updated.channelId));
        }
      },
    }
  );

  const toggleReaction = useMutation(
    ({ messageId, emoji }: { messageId: string; emoji: string }) =>
      post<null, DefaultResponseWithData<IChatMessage>>(
        `/chat/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`,
        null
      ),
    {
      onSuccess: (res) => {
        const updated = res?.data;
        if (updated?.channelId) {
          queryClient.invalidateQueries(chatQueryKeys.messages(updated.channelId));
        }
      },
    }
  );

  const createChannel = useMutation(
    (body: IChatCreateChannelBody) =>
      post<IChatCreateChannelBody, DefaultResponseWithData<IChatChannel>>(
        "/chat/channels",
        body
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(chatQueryKeys.channels);
      },
    }
  );

  const startDm = useMutation(
    (otherUserId: string) =>
      post<{ userId: string }, DefaultResponseWithData<IChatChannel>>(
        "/chat/channels/dm",
        { userId: otherUserId }
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(chatQueryKeys.channels);
      },
    }
  );

  const markRead = useMutation(
    (channelId: string) =>
      post<null, DefaultResponse>(
        `/chat/channels/${channelId}/read`,
        null
      ),
    {
      //Optimistically zero this channel's unread count and shrink the
      //global badge total by the same amount so both the per-channel
      //badge and the sidebar nav badge clear the instant the user
      //opens the channel - no waiting for a server round-trip.
      onMutate: (channelId) => {
        let cleared = 0;
        queryClient.setQueryData<DefaultResponseWithData<IChatChannel[]>>(
          chatQueryKeys.channels,
          (old) => {
            if (!old?.data) return old;
            return {
              ...old,
              data: old.data.map((c) => {
                if (c.id !== channelId) return c;
                if (!c.muted) cleared = c.unreadCount ?? 0;
                return { ...c, unreadCount: 0 };
              }),
            };
          }
        );
        if (cleared > 0) {
          queryClient.setQueryData<DefaultResponseWithData<{ total: number }>>(
            chatQueryKeys.unreadSummary,
            (old) => {
              if (!old?.data) return old;
              return {
                ...old,
                data: { total: Math.max(0, (old.data.total ?? 0) - cleared) },
              };
            }
          );
        }
      },
      onSuccess: () => {
        queryClient.invalidateQueries(chatQueryKeys.readState);
        queryClient.invalidateQueries(chatQueryKeys.channels);
        queryClient.invalidateQueries(chatQueryKeys.unreadSummary);
        queryClient.invalidateQueries(chatQueryKeys.unreadChannels);
      },
    }
  );

  const addChannelMembers = useMutation(
    ({ channelId, userIds }: { channelId: string; userIds: string[] }) =>
      post<{ userIds: string[] }, DefaultResponseWithData<IChatChannel>>(
        `/chat/channels/${channelId}/members`,
        { userIds }
      ),
    {
      onSuccess: (res) => {
        const updated = res?.data;
        if (!updated) return;
        queryClient.setQueryData<DefaultResponseWithData<IChatChannel[]>>(
          chatQueryKeys.channels,
          (old) => {
            if (!old?.data) return old;
            return {
              ...old,
              data: old.data.map((c) => (c.id === updated.id ? updated : c)),
            };
          }
        );
      },
    }
  );

  const removeChannelMember = useMutation(
    ({ channelId, userId }: { channelId: string; userId: string }) =>
      del<DefaultResponseWithData<IChatChannel>>(
        `/chat/channels/${channelId}/members/${userId}`
      ),
    {
      onSuccess: (res) => {
        const updated = res?.data;
        if (!updated) return;
        queryClient.setQueryData<DefaultResponseWithData<IChatChannel[]>>(
          chatQueryKeys.channels,
          (old) => {
            if (!old?.data) return old;
            return {
              ...old,
              data: old.data.map((c) => (c.id === updated.id ? updated : c)),
            };
          }
        );
      },
    }
  );

  const setChannelMuted = useMutation(
    ({ channelId, muted }: { channelId: string; muted: boolean }) =>
      post<{ muted: boolean }, DefaultResponseWithData<IChatChannel>>(
        `/chat/channels/${channelId}/mute`,
        { muted }
      ),
    {
      //Patch the cached channel in place so the toggle feels instant.
      //Server confirmation arrives via the response and a SignalR
      //`ChannelUpdated` event broadcast to the caller's other sessions.
      //Also nudge the global unread summary - muting/unmuting a noisy
      //channel changes the badge total and we want that reflected
      //immediately rather than waiting for the next poll.
      onSuccess: (res) => {
        const updated = res?.data;
        if (!updated) return;
        queryClient.setQueryData<DefaultResponseWithData<IChatChannel[]>>(
          chatQueryKeys.channels,
          (old) => {
            if (!old?.data) return old;
            return {
              ...old,
              data: old.data.map((c) => (c.id === updated.id ? updated : c)),
            };
          }
        );
        queryClient.invalidateQueries(chatQueryKeys.unreadSummary);
        queryClient.invalidateQueries(chatQueryKeys.unreadChannels);
      },
    }
  );

  return {
    users,
    channels,
    readState,
    useMessages,
    sendMessage,
    editMessage,
    toggleReaction,
    createChannel,
    startDm,
    markRead,
    setChannelMuted,
    addChannelMembers,
    removeChannelMember,
  };
}

// ---------------------------------------------------------------------------
// Lightweight hook for places that only need the global unread badge total
// (sidebar nav, topbar, etc). Hits the dedicated `/chat/unread-summary`
// endpoint which is a single COUNT(*) on the server, so the badge can
// render quickly on a cold dashboard load without having to wait for the
// full channel list to materialise. Also subscribes to SignalR events so
// the badge stays live when the user is on a non-chat route.
//
// If the chat page is already mounted and has a populated channel list in
// the cache, we use that as a synchronous initial value so the badge
// number doesn't briefly flash to 0 while the summary request is in
// flight.
// ---------------------------------------------------------------------------
export function useChatUnreadTotal(): number {
  const { get } = useApiHelper();
  const { loading: authLoading, loggedIn } = useAuth();
  const queryClient = useQueryClient();
  const { chatConnection } = useSignalR();

  const summary = useQuery({
    queryKey: chatQueryKeys.unreadSummary,
    queryFn: () =>
      get<DefaultResponseWithData<{ total: number }>>("/chat/unread-summary"),
    staleTime: 1000 * 30,
    //60s poll covers any case where SignalR drops a message - a one-off
    //extra request per minute is well within budget for the value of a
    //live badge.
    refetchInterval: 1000 * 60,
    enabled: !authLoading && loggedIn,
    //Seed from the channels cache if the chat page has already loaded
    //it, so the badge number never blinks back to zero on remount.
    initialData: () => {
      const cached = queryClient.getQueryData<
        DefaultResponseWithData<IChatChannel[]>
      >(chatQueryKeys.channels);
      const list = cached?.data;
      if (!list) return undefined;
      const total = list
        .filter((c) => !c.muted)
        .reduce((sum, c) => sum + (c.unreadCount ?? 0), 0);
      return {
        success: true,
        message: "",
        data: { total },
      } as DefaultResponseWithData<{ total: number }>;
    },
  });

  useEffect(() => {
    if (!chatConnection) return;
    const refresh = () => {
      queryClient.invalidateQueries(chatQueryKeys.unreadSummary);
      queryClient.invalidateQueries(chatQueryKeys.channels);
    };
    chatConnection.on("MessageReceived", refresh);
    chatConnection.on("ChannelUpdated", refresh);
    chatConnection.on("ChannelCreated", refresh);
    chatConnection.on("ChannelRemoved", refresh);
    return () => {
      chatConnection.off("MessageReceived", refresh);
      chatConnection.off("ChannelUpdated", refresh);
      chatConnection.off("ChannelCreated", refresh);
      chatConnection.off("ChannelRemoved", refresh);
    };
  }, [chatConnection, queryClient]);

  return summary.data?.data?.total ?? 0;
}

// ---------------------------------------------------------------------------
// Per-channel unread digest for the topbar notifications drawer. Returns the
// list of channels the user has unread messages in, with the latest unread
// message preview attached. Live-refreshed off SignalR events so a new
// message immediately surfaces a row even on non-chat routes.
// ---------------------------------------------------------------------------
export function useChatNotifications() {
  const { get } = useApiHelper();
  const { loading: authLoading, loggedIn } = useAuth();
  const queryClient = useQueryClient();
  const { chatConnection } = useSignalR();

  const query = useQuery({
    queryKey: chatQueryKeys.unreadChannels,
    queryFn: () =>
      get<DefaultResponseWithData<IChatUnreadChannel[]>>(
        "/chat/unread-channels"
      ),
    staleTime: 1000 * 30,
    refetchInterval: 1000 * 60,
    enabled: !authLoading && loggedIn,
  });

  useEffect(() => {
    if (!chatConnection) return;
    const refresh = () => {
      queryClient.invalidateQueries(chatQueryKeys.unreadChannels);
    };
    chatConnection.on("MessageReceived", refresh);
    chatConnection.on("MessageEdited", refresh);
    chatConnection.on("ChannelUpdated", refresh);
    chatConnection.on("ChannelCreated", refresh);
    chatConnection.on("ChannelRemoved", refresh);
    return () => {
      chatConnection.off("MessageReceived", refresh);
      chatConnection.off("MessageEdited", refresh);
      chatConnection.off("ChannelUpdated", refresh);
      chatConnection.off("ChannelCreated", refresh);
      chatConnection.off("ChannelRemoved", refresh);
    };
  }, [chatConnection, queryClient]);

  return {
    rows: query.data?.data ?? [],
    isLoading: query.isLoading,
  };
}

